import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingCart, Video, Search, TrendingUp, Target, Rocket, LineChart, Users, Lightbulb, BookOpen, Zap } from "lucide-react";
import { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "@/lib/utils";

interface ProductCardProps {
  product: ShopifyProduct;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const addItem = useCartStore(state => state.addItem);
  const navigate = useNavigate();
  
  const { node } = product;
  const firstVariant = node.variants.edges[0]?.node;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;

  // Map product titles to representative icons
  const getProductIcon = () => {
    const title = node.title.toLowerCase();
    const description = node.description.toLowerCase();
    
    if (title.includes('discovery') || title.includes('consultation') || title.includes('call')) {
      return Video;
    } else if (title.includes('diagnostic') || title.includes('audit') || title.includes('analysis')) {
      return Search;
    } else if (title.includes('accelerator') || title.includes('program')) {
      return Rocket;
    } else if (title.includes('blueprint') || title.includes('strategy')) {
      return Target;
    } else if (title.includes('workshop') || title.includes('training')) {
      return Users;
    } else if (title.includes('mentorship') || title.includes('coaching')) {
      return Lightbulb;
    } else if (title.includes('pitch') || title.includes('presentation')) {
      return LineChart;
    } else if (title.includes('course') || title.includes('guide')) {
      return BookOpen;
    } else if (description.includes('growth') || description.includes('scale')) {
      return TrendingUp;
    } else if (description.includes('startup') || description.includes('launch')) {
      return Zap;
    }
    return Target;
  };

  const ProductIcon = getProductIcon();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (firstVariant) {
      addItem({
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || []
      });
    }
  };

  const handleCardClick = () => {
    navigate(`/product/${node.handle}`);
  };

  return (
    <Card className="group overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer" onClick={handleCardClick}>
      <div className="aspect-square overflow-hidden bg-secondary/10">
        {image ? (
          <img
            src={image.url}
            alt={image.altText || node.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-primary/5 to-transparent animate-pulse" />
            <ProductIcon className="h-20 w-20 text-primary relative z-10 drop-shadow-lg" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <CardHeader>
        <CardTitle className="line-clamp-1">
          {node.handle === 'founder-consulting-package' ? 'Founding Team Sprint' : node.title}
        </CardTitle>
        <CardDescription className="line-clamp-2">{node.description}</CardDescription>
      </CardHeader>
      <CardFooter className="gap-2">
        <Button onClick={handleAddToCart} className="w-full" disabled={!firstVariant?.availableForSale}>
          <ShoppingCart className="mr-2 h-4 w-4" />
          {firstVariant?.availableForSale ? 'Add to Cart' : 'Out of Stock'}
        </Button>
      </CardFooter>
    </Card>
  );
};
